import {
  useSeatManagementPortal,
  useUpdateSeatManagementPortal,
} from "api/customerPortal";
import { NewModal } from "components";
import { CHECKOUT_URL } from "constants/pages";
import { MessagesContainer } from "features/inviteUser/MessagesContainer";
import { useCallback, useEffect, useState } from "react";
import { useToasts } from "react-toast-notifications";
import { type ITeamUserObject } from "types/TeamUsersResponse";
import { getErrorMessage } from "utils";
import { CTAButton } from "../CTAButton";
import { MultiEmailInput } from "../email/MultiEmailInput";
import {
  clearPendingEmails,
  savePendingEmails,
} from "../utils/pendingEmailsStorage";
import { ModalHeader } from "./ModalHeader";
import { TemplateDropdown } from "./TemplateDropdown";
import { useInviteUser } from "./useInviteUser";

export interface IInviteUserProps {
  teamUsers: ITeamUserObject[] | undefined;
  isOpen: boolean;
  onClose?: () => void;
  handleInviteUsers: (emails: string[], templateId?: number) => void;
  isLoading: boolean;
  isUpdatingBGData: boolean;
}

export function InviteUserModal({
  teamUsers,
  isOpen,
  onClose,
  handleInviteUsers,
  isLoading,
  isUpdatingBGData,
}: IInviteUserProps) {
  const { addToast } = useToasts();
  const {
    mutate: fireSeatManagementPortal, // Cancel scheduled seat change
    isLoading: isLoadingSeatManagementPortal,
  } = useSeatManagementPortal();
  const {
    mutate: fireUpdateSeatManagementPortal,
    isLoading: isLoadingUpdateSeatManagementPortal,
  } = useUpdateSeatManagementPortal();
  const {
    assignedAndPendingSeats,
    totalSeats,
    currentTeamId,
    isLimitExceeded,
    exceededMailCount,
    emailsList,
    emailsWithNoTeamId,
    emailsGroupedBystatus,
    messages,
    buttonRestProps,
    hasScheduledSeatChange,
    reductionSeatCount,
    targetTotalSeatCount,
    setEmailsMap,
    addEmails,
    removeEmail,
    templates,
    defaultTemplateId,
    isTemplatesLoading,
  } = useInviteUser(
    teamUsers,
    isLoading ||
      isLoadingSeatManagementPortal ||
      isLoadingUpdateSeatManagementPortal,
    isOpen,
  );

  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null,
  );

  useEffect(() => {
    if (defaultTemplateId !== null && selectedTemplateId === null) {
      setSelectedTemplateId(defaultTemplateId);
    }
  }, [defaultTemplateId, selectedTemplateId]);

  // Reset whenever the modal closes — mirrors how useInviteUser resets the
  // email map from an isOpen-driven effect, so the success-close path
  // (closeInviteModal in ManageUsers.onSuccess) also re-pre-selects Default.
  useEffect(() => {
    if (!isOpen) setSelectedTemplateId(null);
  }, [isOpen]);

  const onCloseModal = (): void => {
    setEmailsMap(new Map());
    onClose?.();
  };

  const getPortalUrl = async (
    newSeatCount: number,
    needsPaymentMethod: boolean = false,
  ): Promise<string> => {
    const data = await fireSeatManagementPortal({
      data: {
        cancel_url: CHECKOUT_URL.CANCEL,
        success_url: CHECKOUT_URL.SUCCESS,
        current_seat_count: newSeatCount,
        needs_payment_method: needsPaymentMethod,
      },
    });
    // `!needsPaymentMethod` To prevent circular API calls
    if (data?.needs_payment_method && !needsPaymentMethod) {
      return getPortalUrl(newSeatCount, true);
    } else if (data?.url) {
      return data.url;
    }

    throw new Error("Failed to get portal URL");
  };

  const handleAddSeats = async (
    emails: string[],
    newSeatCount: number,
    hasSchedule: boolean,
  ) => {
    // Save emails to localStorage when increasing seats
    savePendingEmails(emails);

    try {
      if (hasSchedule) await fireUpdateSeatManagementPortal();
      const portalUrl = await getPortalUrl(newSeatCount);
      window.location.href = portalUrl;
    } catch (error) {
      addToast(
        getErrorMessage(error, "Failed to add seats. Please try again!"),
        { appearance: "error" },
      );
    }
  };

  const handleSubmit = useCallback(async () => {
    if (!emailsWithNoTeamId.length) return;
    const emailsToAdd = emailsGroupedBystatus.success
      .filter(({ teamId }) => !teamId)
      .map(({ email }) => email);

    if (isLimitExceeded && exceededMailCount) {
      handleAddSeats(
        emailsToAdd,
        totalSeats + exceededMailCount,
        hasScheduledSeatChange,
      );
      return;
    }

    if (reductionSeatCount && targetTotalSeatCount > reductionSeatCount) {
      await fireUpdateSeatManagementPortal();
    }

    const templateIdToSend =
      selectedTemplateId !== null ? selectedTemplateId : undefined;
    handleInviteUsers(emailsToAdd, templateIdToSend);
    clearPendingEmails();
  }, [
    emailsGroupedBystatus.success,
    emailsWithNoTeamId.length,
    isLimitExceeded,
    exceededMailCount,
    hasScheduledSeatChange,
    handleAddSeats,
    reductionSeatCount,
    targetTotalSeatCount,
    handleInviteUsers,
    selectedTemplateId,
  ]);

  return isOpen ? (
    <NewModal
      modalHeaderStyle={{ paddingX: 3 }}
      bodyStyle={{ paddingX: 3 }}
      modalContentStyle={{
        position: "relative",
        top: "auto",
        left: "auto",
        transform: "none",
        margin: "auto",
        maxHeight: "calc(100vh - 4rem)",
        overflow: "auto",
      }}
      isOpen={isOpen}
      onClose={onCloseModal}
      title={
        <ModalHeader
          assignedAndPendingSeats={assignedAndPendingSeats}
          totalSeats={totalSeats}
        />
      }
      modalBody={
        <MultiEmailInput
          emailsList={emailsList}
          currentTeamId={currentTeamId}
          addEmails={addEmails}
          removeEmail={removeEmail}
          onSubmit={handleSubmit}
        >
          <>
            <TemplateDropdown
              templates={templates}
              value={selectedTemplateId}
              onChange={setSelectedTemplateId}
              isLoading={isTemplatesLoading}
            />
            <MessagesContainer
              messages={messages}
              showMsgs={!isUpdatingBGData}
            />
            <CTAButton {...buttonRestProps} />
          </>
        </MultiEmailInput>
      }
    />
  ) : null;
}
